import COLOR from '@fx-ui/color';
import { ConfigProvider } from '@fx-ui/fine-design';
import { CorpThemeColor } from '~/interfaces';
import { hexToRgbJson } from './color';
import { get, assign } from '@libs/shared';

/**
 * 通过主题色字符串只获取主题色系
 * @param themeColor
 */
export const getPrimaryColorByTheme = (themeColor?: CorpThemeColor) => {
    return {
        primaryColor: get(COLOR, `${themeColor}6`, COLOR.teal6) as string,
        primaryActiveColor: get(COLOR, `${themeColor}7`, COLOR.teal7) as string,
        primaryHoverColor: get(COLOR, `${themeColor}5`, COLOR.teal5) as string,
        primaryBgColor: get(COLOR, `${themeColor}1`, COLOR.teal1) as string,
        primaryBgActiveColor: get(COLOR, `${themeColor}3`, COLOR.teal3) as string,
        primaryBgHoverColor: get(COLOR, `${themeColor}2`, COLOR.teal2) as string,
        primaryBorderColor: get(COLOR, `${themeColor}3`, COLOR.teal3) as string,
        primaryBorderHoverColor: get(COLOR, `${themeColor}4`, COLOR.teal4) as string,
        primaryTextColor: get(COLOR, `${themeColor}2`, COLOR.teal6) as string,
        primaryTextActiveColor: get(COLOR, `${themeColor}2`, COLOR.teal7) as string,
        primaryTextHoverColor: get(COLOR, `${themeColor}2`, COLOR.teal5) as string
    };
};

/**
 * 用来设置项目中主题色
 */
export const setThemeColor = (themeColor?: CorpThemeColor) => {
    if (themeColor) {
        document.documentElement.setAttribute('theme-color', themeColor);
    } else {
        if (document.documentElement.hasAttribute('theme-color')) {
            document.documentElement.removeAttribute('theme-color');
        }
    }
};

/**
 * 设置一体化Token
 */
const generateThemeToken = () => {
    const token = {
        controlItemFontWeightActive: '600',
        // 颜色
        colorText: 'var(--fd-color-transparent-1)',
        colorSplitWhite: 'var(--fd-color-trans-white-3)',
        // 边框
        borderRadiusXs: '2px',
        borderRadiusSm: '4px',
        borderRadius: '6px',
        borderRadiusLg: '6px',
        borderRadiusXl: '10px',
        // 宽度
        controlOutlineWidth: '0',
        // 阴影
        boxShadow:
            '0 0 2px 0 rgba(19, 29, 46, .02), 0 4px 8px 0 rgba(19, 29, 46, .06), 0 4px 24px 6px rgba(19, 29, 46, .04)',
        boxShadowSecondary:
            '0 0 4px 0 rgba(19, 29, 46, .05), 0 6px 16px -1px rgba(19, 29, 46, .06), 0 6px 32px 8px rgba(19, 29, 46, .04)',
        boxShadowTertiary:
            '0 0 5px 0 rgba(19, 29, 46, .06), 0 10px 24px -2px rgba(19, 29, 46, .1), 0 10px 48px 12px rgba(19, 29, 46, .04)',
        boxShadowSubtle: '0 0 2px 0 rgba(19, 29, 46, .02), 0 1px 4px 0 rgba(19, 29, 46, .06)'
    };
    const componentsToken = {
        Button: {
            textHoverBackground: 'transparent',
            textActiveBackground: 'transparent',
            textDangerHoverBackground: 'transparent',
            textDangerActiveBackground: 'transparent'
        },
        Input: {
            activeShadow: 'none',
            warningActiveShadow: 'none',
            errorActiveShadow: 'none'
        }
    };

    const capitalize = (str: string) => str.slice(0, 1).toUpperCase() + str.slice(1);

    // 有色色板覆盖
    for (const colorKey of [
        'red',
        'orange',
        'yellow',
        'maize',
        'lime',
        'lightgreen',
        'green',
        'teal',
        'cyan',
        'blue',
        'indigo',
        'violet',
        'purple',
        'lilac',
        'bluegrey'
    ]) {
        for (let i = 1; i <= 10; i++) {
            assign(token, {
                [`color${capitalize(colorKey)}${i}`]: get(COLOR, `${colorKey}${i}`)
            });
        }
    }
    // 透明白色
    for (let i = 1; i <= 13; i++) {
        assign(token, { [`colorTransWhite${i}`]: get(COLOR, `transparentWhite${i}`) });
    }
    // 透明色与灰色
    for (const colorKey of ['transparent', 'grey']) {
        for (let i = 1; i <= 13; i++) {
            assign(token, { [`color${capitalize(colorKey)}${i}`]: get(COLOR, `${colorKey}${i}`) });
        }
    }

    return {
        token,
        components: componentsToken
    };
};

/**
 * 主题配置
 * @param themToken
 */
const getThemeConfig = (themToken: object = {}) => {
    const { token, components } = generateThemeToken();
    return {
        theme: {
            token: { ...themToken, ...token },
            components
        }
    };
};

const initGlobalTheme = (themeColor?: CorpThemeColor) => {
    const { primaryColor } = getPrimaryColorByTheme(themeColor);
    const rgbObj = hexToRgbJson(primaryColor);
    const token: { [key: string]: string } = {};
    for (let i = 1; i <= 10; i++) {
        token[`colorBrand${i}`] = get(COLOR, `${themeColor}${i}`, get(COLOR, `teal${i}`));
    }
    token['colorControlOutline'] = `rgba(${rgbObj.r},${rgbObj.g},${rgbObj.b},${0.1})`;
    ConfigProvider.config(getThemeConfig(token));
    setThemeColor(themeColor);
};

/**
 * 配置全局主题色
 * 优先级: 租户配置 > 部署环境配置 > 平台默认配置
 * @param theme
 * @param theme.themeEnabled
 * @param theme.themeColor
 */
export const configGlobalTheme = (theme?: {
    themeEnabled: boolean;
    themeColor?: CorpThemeColor;
}) => {
    if (theme?.themeEnabled) {
        initGlobalTheme(theme.themeColor);
    } else if (window.jdy_config?.default_theme?.pc_color) {
        initGlobalTheme(window.jdy_config?.default_theme?.pc_color as CorpThemeColor);
    } else {
        initGlobalTheme();
    }
};
